/**
 * 피감업체 가입 전환 페이지
 * 보고서 공유 링크 접속 후 사업장 담당자가 가입하는 플로우
 * 라우트: /claim-company?token=xxx
 */

import { useState, useEffect, useRef, FormEvent } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button, Card } from '@safetyhub/ui';
import api from '../../lib/api';
import { useAuthStore } from '../../stores/auth';

interface CompanyPreview {
  companyName: string;
  companyAddress: string | null;
  isClaimed: boolean;
}

type PageState = 'loading' | 'ready' | 'claimed' | 'invalid' | 'success';

export default function ClaimCompany() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const shareToken = searchParams.get('token') || '';

  const [pageState, setPageState] = useState<PageState>('loading');
  const [preview, setPreview] = useState<CompanyPreview | null>(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userName, setUserName] = useState('');
  const [ceoNameConfirmation, setCeoNameConfirmation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const { initialize } = useAuthStore();
  const navigateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (navigateTimerRef.current) clearTimeout(navigateTimerRef.current);
    };
  }, []);

  // 공유 토큰으로 Company 미리 조회
  useEffect(() => {
    if (!shareToken) {
      setPageState('invalid');
      return;
    }

    const fetchPreview = async () => {
      try {
        const response = await api.get(`/auth/claim-company/preview`, {
          params: { shareToken },
          skipAuth: true,
        } as Parameters<typeof api.get>[1] & { skipAuth: boolean });
        if (response.data.success) {
          const data: CompanyPreview = response.data.data;
          setPreview(data);
          setPageState(data.isClaimed ? 'claimed' : 'ready');
        } else {
          setPageState('invalid');
        }
      } catch {
        setPageState('invalid');
      }
    };

    fetchPreview();
  }, [shareToken]);

  const validateForm = (): string => {
    if (!email.trim()) return '이메일을 입력해주세요.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return '올바른 이메일 형식이 아닙니다.';
    if (password.length < 8) return '비밀번호는 8자 이상이어야 합니다.';
    if (!userName.trim()) return '이름을 입력해주세요.';
    if (!ceoNameConfirmation.trim()) return '대표자명을 입력해주세요.';
    return '';
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await api.post(
        '/auth/claim-company',
        {
          shareToken,
          email: email.trim(),
          password,
          userName: userName.trim(),
          ceoNameConfirmation: ceoNameConfirmation.trim(),
        },
        { skipAuth: true } as Parameters<typeof api.post>[2] & { skipAuth: boolean },
      );

      if (response.data.success) {
        const { tokens } = response.data.data;
        // 토큰 저장 후 인증 상태 초기화
        localStorage.setItem('accessToken', tokens.accessToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);
        await initialize();
        setPageState('success');
        navigateTimerRef.current = setTimeout(() => navigate('/'), 1500);
      }
    } catch (err: any) {
      const apiError = err?.response?.data?.error;
      if (apiError?.code === 'COMPANY_CEO_NAME_MISMATCH') {
        setError('대표자 정보가 일치하지 않습니다. 사업자등록증의 대표자명을 정확히 입력해주세요.');
      } else if (apiError?.code === 'AUTH_SIGNUP_EMAIL_EXISTS') {
        setError('이미 가입된 이메일입니다. 로그인 페이지로 이동해 주세요.');
      } else if (apiError?.code === 'COMPANY_ALREADY_CLAIMED') {
        setPageState('claimed');
      } else if (apiError?.code === 'COMPANY_BUSINESS_SUSPENDED') {
        setError('현재 휴업 중인 사업자입니다. 관리자에게 문의해주세요.');
      } else if (apiError?.code === 'COMPANY_BUSINESS_CLOSED') {
        setError('폐업된 사업자입니다. 관리자에게 문의해주세요.');
      } else {
        setError(apiError?.message || '가입 처리 중 오류가 발생했습니다. 다시 시도해주세요.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-600 mx-auto" />
          <p className="mt-3 text-ink-500 text-sm">정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (pageState === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50">
        <div className="max-w-md w-full mx-auto p-8 bg-white rounded-card shadow text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-ink-800 mb-2">유효하지 않은 링크</h2>
          <p className="text-ink-500 text-sm mb-6">
            공유 링크가 만료되었거나 올바르지 않습니다.
          </p>
          <Link to="/login" className="text-brand-600 hover:underline text-sm">
            로그인 페이지로 이동
          </Link>
        </div>
      </div>
    );
  }

  if (pageState === 'claimed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50">
        <div className="max-w-md w-full mx-auto p-8 bg-white rounded-card shadow text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-ink-800 mb-2">이미 가입된 사업자입니다</h2>
          {preview && (
            <p className="text-ink-600 text-sm mb-2">{preview.companyName}</p>
          )}
          <p className="text-ink-500 text-sm mb-6">
            이 사업자등록번호로 이미 계정이 생성되어 있습니다.
            <br />
            기존 계정으로 로그인해주세요.
          </p>
          <Link
            to="/login"
            className="inline-block px-6 py-2 bg-brand-600 text-white rounded-control text-sm font-medium hover:bg-brand-700"
          >
            로그인
          </Link>
        </div>
      </div>
    );
  }

  if (pageState === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50">
        <div className="max-w-md w-full mx-auto p-8 bg-white rounded-card shadow text-center">
          <div className="w-16 h-16 rounded-full bg-brand-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-ink-800 mb-2">가입이 완료되었습니다</h2>
          <p className="text-ink-500 text-sm">대시보드로 이동합니다...</p>
        </div>
      </div>
    );
  }

  // ready — 가입 폼
  return (
    <div className="min-h-screen flex items-center justify-center bg-ink-50 py-12">
      <div className="max-w-md w-full mx-auto">
        {/* p-8(32px)은 Card tier에 없어 문서화된 탈출구(padding="none" + className)를 쓴다. */}
        <Card radius="lg" elevation="md" padding="none" className="p-8">
          {/* 헤더 */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-ink-900">SafetyHub 가입</h1>
            {preview && (
              <div className="mt-3 p-3 bg-blue-50 rounded-card text-left">
                <p className="text-xs text-blue-600 font-medium mb-1">가입 대상 사업장</p>
                <p className="text-sm font-semibold text-blue-900">{preview.companyName}</p>
                {preview.companyAddress && (
                  <p className="text-xs text-blue-700 mt-0.5">{preview.companyAddress}</p>
                )}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 이메일 */}
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">
                이메일 <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@company.com"
                className="w-full px-3 py-2 border border-ink-300 rounded-control text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                required
              />
            </div>

            {/* 비밀번호 */}
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">
                비밀번호 <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="8자 이상"
                className="w-full px-3 py-2 border border-ink-300 rounded-control text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                required
                minLength={8}
              />
            </div>

            {/* 이름 */}
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">
                이름 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="홍길동"
                className="w-full px-3 py-2 border border-ink-300 rounded-control text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                required
              />
            </div>

            {/* 대표자명 (본인 확인) */}
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">
                대표자명 확인 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={ceoNameConfirmation}
                onChange={(e) => setCeoNameConfirmation(e.target.value)}
                placeholder="사업자등록증의 대표자명"
                className="w-full px-3 py-2 border border-ink-300 rounded-control text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                required
              />
              <p className="mt-1 text-xs text-ink-400">
                본인 확인을 위해 사업자등록증에 기재된 대표자명을 정확히 입력해주세요.
              </p>
            </div>

            {/* 에러 메시지 */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-card">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* 가입 버튼 */}
            <Button type="submit" variant="primary" size="lg" disabled={isSubmitting} className="w-full">
              {isSubmitting ? '처리 중...' : '가입하기'}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <p className="text-sm text-ink-500">
              이미 계정이 있으신가요?{' '}
              <Link to="/login" className="text-brand-600 hover:underline font-medium">
                로그인
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
